import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { guided } from '@/llm/guidedRuntime';
import { chatStore } from '@/storage/projectStorePort';
import type { ChatMessage } from '@/llm/types';
import { refineWebApp } from '@/webapp/refine';
import { Button } from '../../ui';

/**
 * Chat drives refinement. The instruction stays in the box until the change succeeds:
 * losing what someone typed because the model failed is the worst possible response to
 * a failure they did not cause.
 */
export default function ChatPanel({
  projectId,
  goal,
  source,
  onSource,
}: {
  projectId: string;
  goal: string;
  source: string;
  onSource: (next: string) => Promise<void>;
}) {
  const { isAuthenticated } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listEnd = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void chatStore()
      .getByProjectId(projectId)
      .then(setMessages)
      .catch(() => setMessages([]));
  }, [projectId]);

  useEffect(() => {
    listEnd.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  async function send(): Promise<void> {
    const instruction = text.trim();
    if (!instruction || busy) return;
    setError(null);
    setBusy(true);
    const now = new Date().toISOString();
    try {
      const userMessage = await chatStore().addMessage(projectId, {
        role: 'user',
        content: instruction,
        timestamp: now,
      });
      setMessages((m) => [...m, userMessage]);

      const result = await refineWebApp(
        source,
        instruction,
        { generate: (input) => guided().generate(input) },
        isAuthenticated,
        goal,
      );
      await onSource(result.appCode);

      const reply = await chatStore().addMessage(projectId, {
        role: 'assistant',
        content: 'Done — the preview has the change.',
        timestamp: new Date().toISOString(),
      });
      setMessages((m) => [...m, reply]);
      setText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That change could not be made.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chat-panel">
      <ol className="chat-list">
        {messages.map((m) => (
          <li key={m.id} className={`chat-msg chat-${m.role}`}>
            <span className="type-body">{m.content}</span>
          </li>
        ))}
        <div ref={listEnd} />
      </ol>

      <div className="chat-composer">
        <label className="type-caption field-label" htmlFor="instruction">
          What should I change?
        </label>
        <textarea
          id="instruction"
          className="field"
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
        <Button title="Send" onPress={() => void send()} loading={busy} disabled={!text.trim()} />
      </div>
    </div>
  );
}
